/**
 * Sector Operations — Privacy and Offline Hardening contracts.
 * R39 A12.a (plan docs/plans/sector-operations.md §2.2 SO-P9, SO-P10, A12.a).
 */

import {
  isRestrictedRecordDoc,
  isRestrictedRequestDoc,
  isRestrictedEvidenceSourceItem,
  type EvidenceSourceCollection,
} from './evidence.js'

export type OpsSourceType = 'REQUEST' | 'RECORD' | EvidenceSourceCollection | string

/**
 * Reuses the evidence source table's isRestricted helpers to decide
 * if an ops document (request, record, etc.) carries restricted data.
 */
export function isRestrictedOpsSource(
  sourceType: OpsSourceType,
  doc: Record<string, unknown>,
): boolean {
  if (!doc) return false
  const normalized = String(sourceType || '').trim().toUpperCase()
  if (normalized === 'REQUEST' || normalized === 'OPS_REQUESTS') {
    return isRestrictedRequestDoc(doc)
  }
  if (normalized === 'RECORD' || normalized === 'OPS_RECORDS') {
    return isRestrictedRecordDoc(doc)
  }
  if (
    normalized === 'HOS_PATIENTS' ||
    normalized === 'HOS_VISITS' ||
    normalized === 'HOS_CONSENTS'
  ) {
    return true
  }
  return isRestrictedEvidenceSourceItem(sourceType as EvidenceSourceCollection, doc)
}

export const DEFAULT_OFFLINE_MAX_DAYS = 7
export const MIN_OFFLINE_MAX_DAYS = 1
export const MAX_OFFLINE_MAX_DAYS = 30

export interface OpsPrivacySettingsDoc {
  workspaceId: string
  offlineMaxDays: number
  updatedBy: string
  updatedAt: string
}

export function sanitizeOfflineMaxDays(days: unknown): number {
  const num = typeof days === 'number' ? days : Number.parseInt(String(days ?? ''), 10)
  if (Number.isNaN(num)) return DEFAULT_OFFLINE_MAX_DAYS
  return Math.min(Math.max(num, MIN_OFFLINE_MAX_DAYS), MAX_OFFLINE_MAX_DAYS)
}
